import moment from 'moment'
import { useSelector } from 'react-redux'
import styles from '../InternalExternalDetail.module.css'
import SectionHeader from './SectionHeader'
import CareerHistoryItem from './CareerHistoryItem'

const LOGO_COLORS = ['#2537d8', '#0f766e', '#7c3aed', '#b45309', '#be123c', '#1d4ed8']

const getLogoFromCompany = (company = '') => {
  const parts = company.trim().split(/[\s,]+/).filter(Boolean)
  return parts.length >= 2
    ? `${parts[0][0]}${parts[1][0]}`.toUpperCase()
    : (parts[0]?.[0] || '?').toUpperCase()
}

const getLogoBg = (company = '') =>
  LOGO_COLORS[Math.abs((company.charCodeAt(0) || 0)) % LOGO_COLORS.length]

const formatPeriod = (startDate, endDate, currentJob) => {
  const start = startDate ? moment(startDate).format('MMM YYYY') : '—'
  const end = currentJob ? 'Present' : (endDate ? moment(endDate).format('MMM YYYY') : '—')
  return `${start}  -  ${end}`
}

const mapCareerRecord = (record) => {
  const company = record.CompanyName || '—'
  const responsibilities = record.Consultants_CareerRecords_Responsibilities || []
  const stripSpecial = (s) => s.trim().replace(/^[▸•‣◦⁃∙■→►▶]+\s*/, '')
  const description = responsibilities.length
    ? responsibilities.map((r) => r.Description).filter(Boolean).map(stripSpecial)
    : (record.Description || '').split('\n').map(stripSpecial).filter(Boolean)

  return {
    logo: getLogoFromCompany(company),
    logoBg: getLogoBg(company),
    logoColor: '#fff',
    role: record.Title || '—',
    company,
    period: formatPeriod(record.StartDate, record.EndDate, record.CurrentJob),
    description,
    skills: (record.Consultants_CareerRecordSkills || [])
      .filter((s) => !s.Deleted)
      .map((s) => s.Skills?.SkillName)
      .filter(Boolean),
  }
}

const formatExpPeriod = (exp) => {
  const startYear = exp.start_date_year || exp.start_date?.year || ''
  const startMonth = exp.start_date_month || exp.start_date?.month || ''
  const endYear = exp.end_date_year || exp.end_date?.year || ''
  const endMonth = exp.end_date_month || exp.end_date?.month || ''
  const isCurrent = exp.is_current
  const formatMonthYear = (month, year) => month ? moment(`${year}-${String(month).padStart(2, '0')}`, 'YYYY-MM').format('MMM YYYY') : String(year)
  const start = startYear ? formatMonthYear(startMonth, startYear) : ''
  const end = isCurrent ? 'Present' : (endYear ? formatMonthYear(endMonth, endYear) : '')
  if (!start && !end) return ''
  return `${start}  -  ${end}`
}

const parseExperienceStr = (item) => {
  if (item && typeof item === 'object') {
    const company = item.company_name || item.company || ''
    const role = item.title || item.job_title || ''
    const period = formatExpPeriod(item)
    const description = item.summary ? item.summary.split('\n').map(s => s.trim().replace(/^[•‣◦⁃∙▀-▟■-◿☀-➿-–—·]+\s*/, '')).filter(Boolean) : []
    return { logo: getLogoFromCompany(company), logoBg: getLogoBg(company), logoColor: '#fff', role, company, period, description, skills: [] }
  }
  const s = String(item || '')
  const atIdx = s.indexOf(' at ')
  const role = atIdx > -1 ? s.substring(0, atIdx).trim() : s.trim()
  const rest = atIdx > -1 ? s.substring(atIdx + 4) : ''
  const inIdx = rest.lastIndexOf(' in ')
  const company = inIdx > -1 ? rest.substring(0, inIdx).trim() : rest.trim()
  const period = inIdx > -1 ? rest.substring(inIdx + 4).trim() : ''
  return { logo: getLogoFromCompany(company), logoBg: getLogoBg(company), logoColor: '#fff', role, company, period, description: [], skills: [] }
}

const CareerHistorySection = ({ mode }) => {
  const rawData = useSelector((state) => state.externalUserDetailReducer.rawData)
  const userSchema = useSelector(
    (state) => state.oneUserskillListingReducer?.skillListings?.user_Schema
  )

  const careerRecords = (userSchema?.Profiles?.Consultants_CareerRecords || [])
    .filter((r) => !r.Deleted)
    .map(mapCareerRecord)

  const history = mode === 'external'
    ? (Array.isArray(rawData?.experience) ? rawData.experience.map(parseExperienceStr) : [])
    : careerRecords

  if (!history.length) return null

  return (
    <section className={styles.card}>
      <SectionHeader title="Career History" />
      <div className={styles.historyList}>
        {history.map((job, i) => (
          <CareerHistoryItem key={`${job.company}-${i}`} {...job} />
        ))}
      </div>
    </section>
  )
}

export default CareerHistorySection
